import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { SALON } from '../data/content';

const NAV_ITEMS = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Nail Studio', path: '/nail-studio' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Contact', path: '/contact' },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 btn-transition ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gold/10'
          : 'bg-white/85 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <span className="font-serif text-2xl md:text-3xl font-bold text-neutral-900 tracking-tight">
              {SALON.name}
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `relative text-sm font-medium btn-transition hover:text-gold ${
                    isActive ? 'text-gold' : 'text-neutral-700'
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative">
                    {item.name}
                    <span
                      className={`absolute -bottom-1.5 left-0 h-0.5 bg-gold btn-transition ${
                        isActive ? 'w-full' : 'w-0'
                      }`}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            className="lg:hidden p-2 -mr-2 text-neutral-800 hover:text-gold btn-transition"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile nav overlay */}
      {open && (
        <div className="lg:hidden fixed left-0 right-0 top-16 bg-white z-40 animate-slide-down overflow-y-auto" style={{ height: 'calc(100vh - 4rem)' }}>
          <nav className="flex flex-col p-6 gap-1">
            {NAV_ITEMS.map((item, idx) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `py-4 px-4 font-serif text-2xl border-b border-neutral-100 btn-transition ${
                    isActive ? 'text-gold' : 'text-neutral-800 hover:text-gold'
                  }`
                }
                style={{ animationDelay: `${idx * 0.05}s` }}
              >
                {item.name}
              </NavLink>
            ))}
            <div className="mt-8 space-y-3">
              <a
                href={`tel:${SALON.phoneRaw}`}
                className="block w-full text-center py-3.5 border-2 border-gold text-gold rounded-full font-medium btn-transition hover:bg-gold hover:text-white"
              >
                Call {SALON.phone}
              </a>
              <p className="text-center text-sm text-neutral-500 pt-2">
                {SALON.timing}
              </p>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
